import { type ReactElement, useMemo, useCallback } from 'react'
import { SegmentedControl, Center, type SegmentedControlProps } from '@mantine/core'
import { type MaterialSymbolType, Size } from '@front/model'
import { MaterialSymbolIcon } from '../terros'

export type TSegmentedControlOption<T extends string> = {
  label: string
  value: T
  icon?: MaterialSymbolType
}

type Props<T extends string> = {
  value: T
  onChange: (value: T) => void
  options: TSegmentedControlOption<T>[]
} & Omit<SegmentedControlProps, 'data' | 'value' | 'onChange'>

export const TSegmentedControl = <T extends string>({ value, onChange, options, ...props }: Props<T>): ReactElement => {
  const data = useMemo(() => {
    return options.map((option) => ({
      label: (
        <Center style={{ gap: Size.space.md }}>
          {option.icon && <MaterialSymbolIcon filled={value === option.value} icon={option.icon} size='lg' />}
          {option.label}
        </Center>
      ),
      value: option.value,
    }))
  }, [options, value])

  const handleChange = useCallback(
    (val: string): void => {
      const option = options.find((o) => o.value === val)
      if (option) onChange(option.value)
    },
    [options, onChange]
  )

  return <SegmentedControl color='brand' {...props} data={data} value={value} onChange={handleChange} />
}
